/**
 * User Notification Utilities
 *
 * Provides toast notifications and confirmation dialogs using react-toastify and SweetAlert2.
 * Centralized notification system for consistent user feedback across the application.
 *
 * Features:
 * - Toast notifications with custom transitions and auto-width sizing
 * - SweetAlert2 confirmation dialogs with customizable types
 * - Notification throttling for same messages
 * - Session state notifications for locked records
 *
 * @module notifyUser
 */

import { cssTransition, toast } from 'react-toastify';
import { isValidElement, useEffect, useState } from 'react';
import Swal from 'sweetalert2';

const MAX_TOAST_WIDTH = 400;


/**
 * Confirmation dialog utility using SweetAlert2
 *
 * @param {string} title - Dialog title
 * @param {string} text - Dialog text/message
 * @param {string} confirmText - Confirm button text (default: 'Yes')
 * @param {string} cancelText - Cancel button text (default: 'No')
 * @param {string} type - Dialog type: 'warning', 'info', 'question' (default: 'warning')
 *
 * @returns {Promise<boolean>} - true if confirmed, false if canceled
 */
export const confirmAction = async function( title, text, confirmText = 'Yes', cancelText = 'No', type = 'warning' ) {
    const result = await Swal.fire({
        title: title,
        text: text,
        icon: type,
        showCancelButton: true,
        confirmButtonColor: type === 'warning' ? '#dc2626' : '#15803d',
        cancelButtonColor: '#6b7280',
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        reverseButtons: true
    });

    return result.isConfirmed;
};

/**
 * Custom transition that accepts dynamic duration
 *
 * @param {number} showMs - fade/slide in duration
 * @param {number} hideMs - fade/slide out duration
 * @returns {cssTransition} - usable in toast
 */
export const createToastTransition = function( showMs = 300, hideMs = 300 ) {
    return cssTransition({
        enter: 'toast-enter',
        exit: 'toast-exit',
        duration: [ showMs, hideMs ]
    });
};

/**
 * Returns the calculated width of a string using a temporary container
 *
 * @param {string} msg : a message value
 *
 * @returns {string}
 */
export const getTextWidth = function( msg ) {
    let _text = document.createElement( 'span' );

    document.body.appendChild( _text );
        _text.style.font = 'inherit';
        _text.style.fontSize = '13px';
        _text.style.height = 'auto';
        _text.style.width = 'auto';
        _text.style.position = 'absolute';
        _text.style.whiteSpace = 'nowrap';
        _text.innerHTML = msg;
        _text.innerText = msg;

    let _width = Math.ceil( _text.clientWidth );
    // Keep long notifications compact so their content wraps instead of
    // stretching the toast across the viewport.
    let _formattedWidth = Math.min( _width + 85, MAX_TOAST_WIDTH ) + 'px';

    document.body.removeChild( _text );

    return _formattedWidth;
};

/**
 * Tests if a variable is an object
 *
 * @param {any} val : the object you want to test
 *
 * @returns {boolean}
 */
export const isObject = function( val ) {
    if( val === null ) { return false; }

    return ( ( typeof val === 'function' ) || ( typeof val === 'object' ) );
};

/**
 * General-purpose notification wrapper for toast, sweet alerts ( coming ), and more
 *
 * @param {string|null} e : optional ID
 * @param {string} c : style: success, info, warning, error
 * @param {string|object} m : message or message object
 * @param {string} t : type: 'toast' | 'sweet' | 'notify'
 * @param {HTMLElement|null} b : optional target button ( with spinner reset )
 * @param {number|null} s : show duration
 * @param {number|null} d : delay before auto-dismiss
 * @param {number|null} h : hide duration
 * @param {number|null} w : override width
 * @param {boolean} x : disable close button
 * @param {boolean} y : hide close button
 * @param {string} z : toast position
 *
 * @returns {void}
 */
export const notifyUser = function( e, c, m, t, b = null, s = null, d = null, h = null, w = null, x = false, y = false, z = 'top-right' ) {
    // Global default fallback (applies to non-toast types)
    s = ( s != null ) ? s : 1500;
    d = ( d != null ) ? d : 3000;
    h = ( h != null ) ? h : 1500;

    switch( t ) {
        case 'toast':
            // Toast-specific fallback overrides
            s = ( s != null ) ? s : 9000;
            d = ( d != null ) ? d : 5000;
            h = ( h != null ) ? h : 5000;
            const sendToast = ( msg, idOverride = null ) => {
                const toastID = idOverride || `toast_${Date.now()}`;
                const calcWidth = ( w != null )
                    ? ( typeof w === 'number' ? w + 'px' : w )
                    : ( typeof msg === 'string' ? getTextWidth( msg ) : '350px' );
                const transition = createToastTransition( s, h );

                const isSticky = d === 0;

                const toastMethod =
                    ( c === 'error' )     ? toast.error :
                    ( c === 'info' )      ? toast.info :
                    ( c === 'success' )   ? toast.success : toast.warning;

                toastMethod( msg, {
                    toastId: toastID,
                    position: z,
                    autoClose: isSticky ? false : d,
                    closeOnClick: !isSticky,
                    pauseOnHover: !isSticky,
                    draggable: !isSticky,
                    hideProgressBar: false,
                    closeButton: ( props ) => <ToastCloseButton {...props} x={ x } y={ y } c={ c } />,
                    transition,
                    className: 'custom-toast',
                    bodyClassName: 'custom-toast-body',
                    style: {
                        width: calcWidth,
                        maxWidth: `min(${MAX_TOAST_WIDTH}px, calc(100vw - 2rem))`
                    }
                });
            };

            if( isObject( m ) && !isValidElement( m ) ) {
                Object.entries( m ).forEach( ( [key, value], index ) => {
                    const toastID = `${e || 'msg'}_${key.replace( /\s+/g, '_' )}_${index}`;

                    sendToast( `${key}: ${value}`, toastID );
                });
            }
            else {
                sendToast( m, e || `toast_${Date.now()}` );
            }
        break;

        case 'sweet':
            // SweetAlert2 modal
            const iconMap = {
                'error': 'error',
                'info': 'info',
                'success': 'success',
                'warning': 'warning'
            };

            // Detect if message contains HTML
            const messageContent = isObject( m ) && m.text ? m.text : ( isObject( m ) ? JSON.stringify( m ) : m );
            const containsHtml = typeof messageContent === 'string' && /<[^>]+>/.test( messageContent );

            const swalConfig = {
                icon: iconMap[c] || 'info',
                title: isObject( m ) && m.title ? m.title : ( c === 'error' ? 'Error' : c === 'success' ? 'Success' : 'Notice' ),
                confirmButtonColor: c === 'error' ? '#dc2626' : c === 'success' ? '#15803d' : '#2563eb',
                timer: d === 0 ? undefined : d,
                showConfirmButton: d === 0 || ( isObject( m ) && m.showConfirmButton ),
                allowOutsideClick: !x,
                allowEscapeKey: !x
            };

            // Use 'html' property for HTML content, 'text' for plain text
            if( containsHtml ) {
                swalConfig.html = messageContent;
            }
            else {
                swalConfig.text = messageContent;
            }

            // Add loading state if specified
            if( isObject( m ) && m.showLoading ) {
                swalConfig.didOpen = () => {
                    Swal.showLoading();
                };
            }

            // Add optional callback
            if( isObject( m ) && m.onConfirm ) {
                swalConfig.didClose = m.onConfirm;
            }

            return Swal.fire( swalConfig );
        break;

        case 'notify':
            // coming soon
        break;

        default:
            // coming soon
        break;
    }

    if( b !== null ) {
        setTimeout( function() {
            b.removeAttribute( 'data-kt-indicator' );
        }, d );
    }
};

/**
 * Close button used inside toast notifications.
 * Supports disabling, hiding, theming by type, and hover/press animation.
 *
 * @param {function} closeToast - Callback to dismiss the toast
 * @param {boolean} x - Disable close button
 * @param {boolean} y - Hide close button entirely
 * @param {string} c - Toast type: 'success', 'error', 'info', 'warning'
 *
 * @returns {JSX.Element|null}
 */
export const ToastCloseButton = function({ closeToast, x = false, y = false, c = 'info' }) {
    const [ color, setColor ] = useState( '#666' );

    useEffect( function applyThemeColor() {
        switch( c ) {
            case 'error':
                setColor( '#dc2626' ); // red-600
                break;
            case 'success':
                setColor( '#15803d' ); // green-700
                break;
            case 'warning':
                setColor( '#ca8a04' ); // yellow-600
                break;
            case 'info':
            default:
                setColor( '#2563eb' ); // blue-600
                break;
        }
    }, [ c ]);

    if( y ) {
        return null;
    }

    const handleClick = function( event ) {
        if( !x && typeof closeToast === 'function' ) {
            closeToast( event );
        }
    };

    return (
        <button
            type="button"
            onClick={ handleClick }
            disabled={ x }
            aria-label="close"
            className="transition-all duration-200 ease-in-out hover:scale-110 active:scale-95"
            style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: color,
                background: 'transparent',
                border: 'none',
                position: 'absolute',
                top: '50%',
                right: '12px',
                transform: 'translateY( -50% )',
                cursor: x ? 'not-allowed' : 'pointer',
                opacity: x ? 0.4 : 1
            }}
        >
            ✕
        </button>
    );
};
